<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { EntityListPage, getAuthUsersLookup, type EntityConfig } from '@botto/core';
import {
  smppClientsStore,
  getManagersLookup,
  getPartnersLookup,
} from '../../api/adapters/accounts';
import type { Manager, Partner, SmppClient } from '../../api';
import { DECODING_MODE_ITEMS } from '../../constants/encodingMode';
import { getSmppHostsLookup } from '../../lookups';

const listRef = ref<InstanceType<typeof EntityListPage> | null>(null);
const managersLookup = getManagersLookup();
const partnersLookup = getPartnersLookup();
const authUsersLookup = getAuthUsersLookup();
const smppHostsLookup = getSmppHostsLookup();

const transportTypes = ['TCP', 'TLS'];
const systemTypes = ['VMS', 'OTA', 'VMA', 'NULL'];
const decodingModeItems = DECODING_MODE_ITEMS;

function formatYesNo(value: string | null | undefined): string {
  if (value === 'yes') return 'да';
  if (value === 'no') return 'нет';
  return value ?? '';
}

function connectionStatusCell(cellElement: HTMLElement, cellInfo: { data?: SmppClient }) {
  const row = cellInfo.data ?? {};
  const wrap = document.createElement('span');
  wrap.style.display = 'inline-flex';
  wrap.style.alignItems = 'center';
  wrap.style.gap = '6px';

  const text = document.createElement('span');
  text.textContent = row.connection_status ?? '';
  wrap.appendChild(text);

  if (row.status_description) {
    const icon = document.createElement('i');
    icon.className = 'dx-icon dx-icon-help';
    icon.title = row.status_description;
    icon.style.cursor = 'help';
    icon.style.opacity = '0.65';
    icon.style.fontSize = '16px';
    wrap.appendChild(icon);
  }

  cellElement.appendChild(wrap);
}

function managerPartnerIds(managerId: number | null | undefined): number[] {
  if (managerId == null) return [];
  const manager = managersLookup.byId.value.get(managerId) as Manager | undefined;
  return manager?.partner_ids ?? [];
}

function partnerAllowedForManager(partner: Partner, formData: Record<string, unknown>): boolean {
  const allowed = managerPartnerIds(formData.manager_id as number | null | undefined);
  if (!allowed.length) return false;
  return partner.id != null && allowed.includes(partner.id);
}

onMounted(async () => {
  await Promise.all([
    managersLookup.ensureLoaded(),
    partnersLookup.ensureLoaded(),
    authUsersLookup.ensureLoaded(),
    smppHostsLookup.ensureLoaded(),
  ]);
  listRef.value?.refresh();
});

function updateGridDimensions() {
  listRef.value?.updateGridDimensions();
}

defineExpose({ updateGridDimensions, refresh: () => listRef.value?.refresh() });

const config = computed<EntityConfig<SmppClient>>(() => ({
  title: 'SMPP-клиент',
  titlePlural: 'SMPP (клиенты)',
  popupWidth: 760,
  popupHeight: 620,
  tabs: ['Основное', 'Подключение', 'SMPP'],
  columns: [
    { dataField: 'id', caption: 'ID', dataType: 'number', width: 80 },
    { dataField: 'name', caption: 'Название клиента', width: 160 },
    { dataField: 'description', caption: 'Описание' },
    {
      dataField: 'manager_id',
      caption: 'Менеджер',
      width: 200,
      calculateDisplayValue: (row) => managersLookup.labelOf(row.manager_id),
    },
    {
      dataField: 'partner_id',
      caption: 'Партнёр',
      width: 200,
      calculateDisplayValue: (row) => partnersLookup.labelOf(row.partner_id),
    },
    {
      dataField: 'enabled',
      caption: 'Включён',
      width: 90,
      calculateDisplayValue: (row) => formatYesNo(row.enabled),
    },
    {
      dataField: 'connection_status',
      caption: 'Статус подключения',
      width: 180,
      cellTemplate: connectionStatusCell,
    },
    { dataField: 'smpp_host', caption: 'SMPP host', width: 140 },
    { dataField: 'smsc_username', caption: 'Login SMPP', width: 140 },
    { dataField: 'cps_max', caption: 'Максимальный CPS', dataType: 'number', width: 120 },
    {
      dataField: 'user_id',
      caption: 'Email (user)',
      width: 200,
      allowSorting: false,
      calculateDisplayValue: (row) => authUsersLookup.labelOf(row.user_id),
    },
    {
      dataField: 'created_at',
      caption: 'Создан',
      dataType: 'datetime',
      width: 120,
      format: 'dd.MM.yyyy',
    },
  ],
  fields: [
    { dataField: 'email', label: 'Email', required: true, createOnly: true, tab: 'Основное', colSpan: 2 },
    {
      dataField: 'password',
      label: 'Password',
      required: true,
      createOnly: true,
      tab: 'Основное',
      colSpan: 2,
      editorOptions: { mode: 'password' },
    },
    { dataField: 'name', label: 'Название клиента', tab: 'Основное', colSpan: 2 },
    {
      dataField: 'description',
      label: 'Описание',
      required: true,
      tab: 'Основное',
      editorType: 'dxTextArea',
      colSpan: 2,
      editorOptions: { height: 88, minHeight: 88 },
    },
    { dataField: 'manager_id', label: 'Менеджер', tab: 'Основное', colSpan: 2, lookup: managersLookup },
    {
      dataField: 'partner_id',
      label: 'Партнёр',
      tab: 'Основное',
      colSpan: 2,
      lookup: partnersLookup,
      lookupFilter: partnerAllowedForManager,
      clearWhen: ['manager_id'],
    },
    {
      dataField: 'host',
      label: 'IP, с которого разрешено подключение',
      required: true,
      tab: 'Подключение',
      colSpan: 2,
    },
    {
      dataField: 'cps_max',
      label: 'Максимальный CPS',
      tab: 'Подключение',
      editorType: 'dxNumberBox',
      editorOptions: { min: 1 },
    },
    {
      dataField: 'port',
      label: 'SMPP порт',
      required: true,
      tab: 'SMPP',
      editorType: 'dxNumberBox',
      editorOptions: { min: 1, max: 65535 },
    },
    { dataField: 'smsc_username', label: 'Login SMPP', required: true, tab: 'SMPP', digitsOnly: true },
    {
      dataField: 'smsc_password',
      label: 'Password SMPP',
      tab: 'SMPP',
      editorOptions: { mode: 'password' },
    },
    {
      dataField: 'smpp_host',
      label: 'SMPP host',
      tab: 'SMPP',
      colSpan: 2,
      lookup: smppHostsLookup,
      helpText: 'Каталог GET /smpp_hosts/ (значение = smpp_host)',
    },
    {
      dataField: 'transport_type',
      label: 'Transport type',
      tab: 'SMPP',
      editorType: 'dxSelectBox',
      editorOptions: { items: transportTypes },
    },
    {
      dataField: 'system_type',
      label: 'System type',
      tab: 'SMPP',
      editorType: 'dxSelectBox',
      editorOptions: { items: systemTypes },
    },
    {
      dataField: 'enquire_link_interval',
      label: 'Частота хэндшейков, сек. (enquire link interval)',
      tab: 'SMPP',
      editorType: 'dxNumberBox',
      editorOptions: { min: 1 },
    },
    {
      dataField: 'decoding_mode',
      label: 'Режим кодировки',
      tab: 'SMPP',
      colSpan: 2,
      editorType: 'dxSelectBox',
      helpText: 'По умолчанию — из smpp (message_coding); −1 — авто; 0–8 — фиксированный DCS',
      editorOptions: {
        items: decodingModeItems,
        valueExpr: 'value',
        displayExpr: 'text',
        searchEnabled: false,
        showClearButton: false,
      },
    },
    { dataField: 'created_at', label: 'Создан', editOnly: true, hidden: true },
    { dataField: 'updated_at', label: 'Обновлён', editOnly: true, hidden: true },
  ],
}));
</script>

<template>
  <EntityListPage ref="listRef" embedded :config="config" :store="smppClientsStore" />
</template>
